/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Subtitle Segment List Editor Component (Task 10.5.6)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useEffect, useRef } from 'react';
import {
  Play,
  Scissors,
  Merge,
  Plus,
  Trash2,
  Clock,
  Search,
  SlidersHorizontal,
  ArrowUpDown,
  FileText,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import {
  useSubtitleStudioStore,
  formatTimestampSrt,
  parseTimestamp,
} from '../../stores/useSubtitleStudioStore';

export const SubtitleSegmentList: React.FC = () => {
  const { t } = useTranslation();
  const listContainerRef = useRef<HTMLDivElement>(null);
  const activeCardRef = useRef<HTMLDivElement>(null);

  const {
    activeCueId,
    selectedCueId,
    searchQuery,
    isPlaying,
    setSearchQuery,
    getFilteredCues,
    updateCueText,
    updateCueTiming,
    splitCue,
    mergeCues,
    insertCueAfter,
    deleteCue,
    selectCue,
    seek,
    play,
    setShiftModalOpen,
    reorderAndReindexCues,
  } = useSubtitleStudioStore();

  const filteredCues = getFilteredCues();

  // Auto-scroll active cue into view during playback
  useEffect(() => {
    if (isPlaying && activeCardRef.current) {
      activeCardRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [activeCueId, isPlaying]);

  return (
    <div className="bg-surface rounded-2xl border border-border p-5 shadow-sm space-y-4">
      {/* Top Header: Search and Batch Actions */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pb-3 border-b border-border">
        {/* Search Input */}
        <div className="relative flex-1 max-w-md">
          <Search className="size-4 text-text-muted absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('subtitles.searchPlaceholder')}
            className="w-full text-xs rounded-xl border border-border bg-surface-subtle pl-9 pr-3 py-2 text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>

        {/* Global Action Buttons */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShiftModalOpen(true)}
            className="px-3 py-2 text-xs font-medium rounded-xl border border-border hover:bg-surface-subtle text-text transition-colors flex items-center gap-1.5"
          >
            <SlidersHorizontal className="size-3.5 text-primary" />
            <span>{t('subtitles.shiftTimeBtn')}</span>
          </button>

          <button
            type="button"
            onClick={reorderAndReindexCues}
            className="px-3 py-2 text-xs font-medium rounded-xl border border-border hover:bg-surface-subtle text-text transition-colors flex items-center gap-1.5"
            title="Sort by Start Time"
          >
            <ArrowUpDown className="size-3.5" />
            <span className="hidden sm:inline">Sort</span>
          </button>
        </div>
      </div>

      {/* Cues List */}
      <div
        ref={listContainerRef}
        className="space-y-3 max-h-[580px] overflow-y-auto pr-1"
      >
        {filteredCues.length === 0 ? (
          <div className="text-center py-12 px-4 border border-dashed border-border rounded-xl">
            <div className="p-3 rounded-full bg-surface-subtle text-text-muted inline-block mb-2">
              <FileText className="size-6" />
            </div>
            <h4 className="text-sm font-semibold text-text mb-1">
              {searchQuery ? t('subtitles.noResultsFound') : t('subtitles.emptyCuesTitle')}
            </h4>
            <p className="text-xs text-text-muted max-w-md mx-auto">
              {t('subtitles.emptyCuesDesc')}
            </p>
          </div>
        ) : (
          filteredCues.map((cue, idx) => {
            const isActive = cue.id === activeCueId;
            const isSelected = cue.id === selectedCueId;
            const nextCue = filteredCues[idx + 1];
            const durationSec = +(cue.end - cue.start).toFixed(2);

            return (
              <div
                key={cue.id}
                ref={isActive ? activeCardRef : null}
                onClick={() => selectCue(cue.id)}
                className={`rounded-xl border p-4 transition-all ${
                  isActive
                    ? 'border-primary ring-2 ring-primary/20 bg-primary/5 shadow-xs'
                    : isSelected
                    ? 'border-primary/50 bg-surface-subtle/50'
                    : 'border-border bg-surface hover:border-border/80'
                }`}
              >
                {/* Cue Card Header: Index, Timings, Duration */}
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold px-2 py-0.5 rounded bg-surface-subtle border border-border text-text">
                      #{cue.index}
                    </span>

                    {/* Start Time Editor */}
                    <div className="flex items-center gap-1">
                      <Clock className="size-3 text-text-muted" />
                      <input
                        type="text"
                        defaultValue={formatTimestampSrt(cue.start)}
                        onBlur={(e) => {
                          const parsed = parseTimestamp(e.target.value);
                          updateCueTiming(cue.id, parsed, cue.end);
                        }}
                        className="w-24 text-xs font-mono rounded border border-border bg-surface px-1.5 py-0.5 text-text focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                    </div>

                    <span className="text-text-muted font-mono">→</span>

                    {/* End Time Editor */}
                    <div className="flex items-center gap-1">
                      <input
                        type="text"
                        defaultValue={formatTimestampSrt(cue.end)}
                        onBlur={(e) => {
                          const parsed = parseTimestamp(e.target.value);
                          updateCueTiming(cue.id, cue.start, parsed);
                        }}
                        className="w-24 text-xs font-mono rounded border border-border bg-surface px-1.5 py-0.5 text-text focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                    </div>

                    <span className="text-[11px] text-text-muted font-medium ml-1">
                      {t('subtitles.cueDuration', { sec: String(durationSec) })}
                    </span>
                  </div>

                  {/* Actions Toolbar */}
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        seek(cue.start);
                        play();
                      }}
                      className="p-1.5 rounded-lg border border-border hover:bg-surface-subtle text-text transition-colors"
                      title={t('subtitles.playCueBtn')}
                    >
                      <Play className="size-3.5 fill-current text-primary" />
                    </button>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        splitCue(cue.id);
                      }}
                      className="p-1.5 rounded-lg border border-border hover:bg-surface-subtle text-text transition-colors"
                      title={t('subtitles.splitCueBtn')}
                    >
                      <Scissors className="size-3.5 text-amber-500" />
                    </button>

                    {nextCue && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          mergeCues(cue.id, nextCue.id);
                        }}
                        className="p-1.5 rounded-lg border border-border hover:bg-surface-subtle text-text transition-colors"
                        title={t('subtitles.mergeWithNextBtn')}
                      >
                        <Merge className="size-3.5 text-sky-500" />
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        insertCueAfter(cue.id);
                      }}
                      className="p-1.5 rounded-lg border border-border hover:bg-surface-subtle text-text transition-colors"
                      title={t('subtitles.insertBelowBtn')}
                    >
                      <Plus className="size-3.5 text-emerald-500" />
                    </button>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteCue(cue.id);
                      }}
                      className="p-1.5 rounded-lg border border-border hover:bg-surface-subtle text-rose-500 transition-colors"
                      title={t('subtitles.deleteCueBtn')}
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  </div>
                </div>

                {/* Textarea for Subtitle Cue Text */}
                <textarea
                  rows={2}
                  value={cue.text}
                  onChange={(e) => updateCueText(cue.id, e.target.value)}
                  className="w-full text-xs rounded-lg border border-border bg-surface px-3 py-2 text-text focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none font-sans"
                />

                {/* Optional Bilingual translation cue */}
                {cue.translation && (
                  <div className="mt-2 text-xs text-text-muted italic bg-surface-subtle p-2 rounded-lg border border-border">
                    {cue.translation}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
